import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaBuilding, FaCalendarAlt, FaCheckCircle, FaChevronRight, FaSyncAlt, FaTools, FaBullseye, FaExclamationCircle } from 'react-icons/fa';
import API_BASE_URL from './config/api';
import { readMaintenanceUser } from './maintenanceAccess';
import { useMaintenanceAccess } from './MaintenanceAccessContext';
import { summarizeOverviewSchedule, summarizeMechanicalVisits, getVisitTiming, torontoDateISO } from './maintenanceOverviewData';
import './MaintenanceOverview.css';

const WORK_AREAS = [
  { key: 'in-suite', label: 'In-Suite Maintenance', icon: FaBuilding, accent: '#1F3A68' },
  { key: 'mech-management', label: 'Mechanical Maintenance', icon: FaTools, accent: '#C68A1C' },
  { key: 'quota-tracker', label: 'Service Tracker', icon: FaBullseye, accent: '#7c3aed' },
];
const TYPE_LABELS = {
  fancoil: 'Fan Coil', 'fan coil': 'Fan Coil', heatpump: 'Heat Pump', 'heat pump': 'Heat Pump',
  dryer: 'Dryer Vent', 'dryer vent': 'Dryer Vent', 'dryer-vent': 'Dryer Vent', dryer_vent: 'Dryer Vent',
  erv: 'ERV', project: 'Project',
};
const validSchedule = body => Array.isArray(body?.buildings);
const validContext = body => Boolean(body?.weekStart && body?.weekEnd
  && (Array.isArray(body.mechanicalVisits) || (body.mechanicalVisits === null && body.errors?.mechanicalVisits)));

function displayType(value) {
  return String(value || 'Maintenance').split(',').map(type => (
    TYPE_LABELS[type.trim().toLowerCase()] || type.trim()
  )).join(', ');
}
function displayDate(date, options = { month: 'short', day: 'numeric' }) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC', ...options }).format(new Date(`${date}T12:00:00Z`));
}
function dateRange(start, end) {
  return start === end ? displayDate(start) : `${displayDate(start)} – ${displayDate(end)}`;
}

// Load independently: an unavailable source must not turn into a zero count.
function useOverviewSource(url, validate, refresh, enabled = true) {
  const [state, setState] = useState({ loading: true, data: null, error: false });
  useEffect(() => {
    if (!enabled) {
      setState({ loading: false, data: null, error: false });
      return undefined;
    }
    const controller = new AbortController();
    setState({ loading: true, data: null, error: false });
    (async () => {
      try {
        const token = localStorage.getItem('managerToken');
        const response = await fetch(url, {
          signal: controller.signal,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) throw new Error('Unable to load overview');
        const body = await response.json();
        if (!validate(body)) throw new Error('Invalid overview response');
        if (!controller.signal.aborted) setState({ loading: false, data: body, error: false });
      } catch (error) {
        if (!controller.signal.aborted) setState({ loading: false, data: null, error: true });
      }
    })();
    return () => controller.abort();
  }, [url, validate, refresh, enabled]);
  return state;
}

export default function MaintenanceOverview() {
  const [refresh, setRefresh] = useState(0);
  const [selectedDivision, setDivision] = useState('In-suite');
  const { canViewTab } = useMaintenanceAccess();
  const canInSuite = canViewTab('in-suite');
  const canMechanical = canViewTab('mech-management');
  const division = selectedDivision === 'In-suite' && canInSuite ? 'In-suite'
    : canMechanical ? 'Mechanical' : 'In-suite';
  const user = useMemo(() => readMaintenanceUser(), []);
  const today = torontoDateISO();
  const hour = Number(new Intl.DateTimeFormat('en-US', { hour: 'numeric', hourCycle: 'h23', timeZone: 'America/Toronto' }).format(new Date()));
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const name = user?.first_name || user?.firstName || user?.username || '';
  const schedule = useOverviewSource(`${API_BASE_URL}/api/maintenance-schedule/buildings`, validSchedule, refresh, canInSuite);
  const context = useOverviewSource(`${API_BASE_URL}/api/maintenance/overview-context`, validContext, refresh, canMechanical);
  const summary = useMemo(() => summarizeOverviewSchedule(schedule.data?.buildings || [], today), [schedule.data, today]);
  const weekMatches = !context.data || (context.data.weekStart === summary.week.startDate && context.data.weekEnd === summary.week.endDate);
  const mechanicalError = context.error || !weekMatches || Boolean(context.data?.errors?.mechanicalVisits);
  const issues = context.data?.mechanicalIssuesYesterday;
  const yesterday = new Date(Date.parse(`${today}T12:00:00Z`) - 86400000).toISOString().slice(0, 10);
  const issuesError = context.error || !weekMatches || Boolean(context.data?.errors?.mechanicalIssuesYesterday)
    || (!context.loading && (!issues || !Number.isInteger(issues.count) || issues.count < 0 || issues.date !== yesterday));
  const mechanicalVisits = useMemo(
    () => mechanicalError ? [] : summarizeMechanicalVisits(context.data?.mechanicalVisits || [], today),
    [context.data, mechanicalError, today],
  );
  const visits = useMemo(() => [
    ...summary.inSuiteVisits.map(visit => ({ ...visit, division: 'In-suite', to: '/maintenance/in-suite' })),
    ...mechanicalVisits.map(visit => ({ ...visit, division: 'Mechanical', to: '/maintenance/mech-management' })),
  ].sort((a, b) => a.startDate.localeCompare(b.startDate) || a.buildingName.localeCompare(b.buildingName)), [summary, mechanicalVisits]);
  const loading = schedule.loading || context.loading;
  const visibleVisits = visits.filter(visit => visit.division === division);
  const isInSuiteSelected = division === 'In-suite';
  const listLoading = isInSuiteSelected ? schedule.loading : context.loading;
  const listError = isInSuiteSelected ? schedule.error : mechanicalError;

  const areaDetail = key => {
    if (key === 'quota-tracker') return 'View targets and follow-ups';
    const isInSuite = key === 'in-suite';
    if (isInSuite ? schedule.loading : context.loading) return 'Loading this week…';
    if (isInSuite ? schedule.error : mechanicalError) return 'Open schedule';
    const count = isInSuite ? summary.inSuiteVisits.length : mechanicalVisits.length;
    return `${count} ${isInSuite ? (count === 1 ? 'maintenance' : 'maintenances') : (count === 1 ? 'visit' : 'visits')} this week`;
  };

  return (
    <main className="internal-overview">
      <header className="internal-overview__header">
        <div>
          <time className="internal-overview__today" dateTime={today}>
            {displayDate(today, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </time>
          <h1>{greeting}{name ? `, ${name}` : ''}</h1>
          <p>This week’s maintenance and the follow-ups that need attention.</p>
        </div>
        <button className="internal-overview__refresh" type="button" onClick={() => setRefresh(value => value + 1)} disabled={loading}>
          <FaSyncAlt aria-hidden="true" /> Refresh
        </button>
      </header>

      {WORK_AREAS.some(area => canViewTab(area.key)) && <section aria-labelledby="overview-quick-links" className="internal-overview__quick-links">
        <h2 id="overview-quick-links">Quick links</h2>
        <div className="internal-overview__work-areas">
          {WORK_AREAS.filter(area => canViewTab(area.key)).map(({ key, label, icon: Icon, accent }) => (
            <Link className="internal-overview__work-area" key={key} to={`/maintenance/${key}`} style={{ '--area-accent': accent, '--area-background': `${accent}14` }}>
              <span className="internal-overview__area-icon"><Icon aria-hidden="true" /></span>
              <span className="internal-overview__area-copy">
                <strong>{label}<FaChevronRight aria-hidden="true" /></strong>
                <span className="internal-overview__area-detail">{areaDetail(key)}</span>
              </span>
            </Link>
          ))}
        </div>
      </section>}

      <div className="internal-overview__panels">
        {(canInSuite || canMechanical) && <section className="internal-overview__panel" aria-labelledby="overview-week">
          <div className="internal-overview__panel-heading">
            <h2 id="overview-week"><FaCalendarAlt aria-hidden="true" /> Maintenances this week</h2>
            <span className="internal-overview__panel-meta">{dateRange(summary.week.startDate, summary.week.endDate)}</span>
          </div>
          <div className="internal-overview__division-filter">
            <div className="internal-overview__division-toggle" role="group" aria-label="Maintenance type">
              {canInSuite && <button type="button" aria-pressed={isInSuiteSelected} aria-controls="overview-weekly-visits" onClick={() => setDivision('In-suite')}>
                In-suite{!schedule.loading && !schedule.error && <span> ({summary.inSuiteVisits.length})</span>}
              </button>}
              {canMechanical && <button type="button" aria-pressed={!isInSuiteSelected} aria-controls="overview-weekly-visits" onClick={() => setDivision('Mechanical')}>
                Mechanical{!context.loading && !mechanicalError && <span> ({mechanicalVisits.length})</span>}
              </button>}
            </div>
          </div>
          {listError && <p role="status" className="internal-overview__source-note">{isInSuiteSelected ? 'In-suite maintenance couldn’t be loaded.' : 'Mechanical visits couldn’t be loaded.'}</p>}
          {listLoading && <p className="internal-overview__empty" role="status">Loading this week’s maintenance…</p>}
          {!listLoading && !listError && !visibleVisits.length && (
            <p className="internal-overview__empty">{isInSuiteSelected ? 'No in-suite maintenance scheduled this week.' : 'No mechanical visits scheduled this week.'}</p>
          )}
          <ul className="internal-overview__visits" id="overview-weekly-visits" aria-label={`${division} maintenances this week`}>
            {visibleVisits.map(visit => {
              const timing = getVisitTiming(visit, today);
              const divisionClass = visit.division === 'Mechanical' ? 'mechanical' : 'insuite';
              const timingClass = timing === 'In progress' || timing === 'Completed' ? 'active'
                : ['Starts today', 'Tomorrow', 'In 2 days', 'In 3 days'].includes(timing) ? 'soon' : 'upcoming';
              return (
                <li key={`${divisionClass}:${visit.key}`}>
                  <Link to={visit.to} className="internal-overview__visit">
                    <span className={`internal-overview__timing internal-overview__timing--${timingClass}`}>{timing}</span>
                    <div className="internal-overview__visit-copy">
                      <div className="internal-overview__visit-title">
                        <strong>{visit.buildingName}</strong>
                        <span className={`internal-overview__division internal-overview__division--${divisionClass}`}>{visit.division === 'In-suite' ? displayType(visit.scopeLabel || visit.maintenanceType) : visit.division}</span>
                      </div>
                      <span className="internal-overview__visit-detail">{dateRange(visit.startDate, visit.endDate)}</span>
                    </div>
                    <FaChevronRight aria-hidden="true" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>}

        {(canInSuite || canMechanical) && <section className="internal-overview__panel internal-overview__attention" aria-labelledby="overview-attention">
          <div className="internal-overview__panel-heading">
            <h2 id="overview-attention"><FaExclamationCircle aria-hidden="true" /> Needs attention</h2>
            <span className="internal-overview__panel-meta">Follow-ups</span>
          </div>
          {canInSuite && <div className="internal-overview__attention-item">
            {schedule.loading ? <p role="status">Checking maintenance closeout…</p> : schedule.error ? (
              <p role="status">Maintenance closeout could not be checked. Try Refresh.</p>
            ) : summary.closeoutCount > 0 ? (
              <>
                <div className="internal-overview__attention-copy"><span className="internal-overview__attention-dot" /><strong>{summary.closeoutCount} in-suite {summary.closeoutCount === 1 ? 'maintenance needs' : 'maintenances need'} closeout and invoice review</strong></div>
                <p>Scheduled end date has passed. Confirm completion, reports, and invoicing.</p>
                <Link to="/maintenance/in-suite" className="internal-overview__action">Review maintenances <FaArrowRight aria-hidden="true" /></Link>
              </>
            ) : <p className="internal-overview__clear"><FaCheckCircle aria-hidden="true" /> No open in-suite cycles past their scheduled end date.</p>}
          </div>}
          {canMechanical && <div className="internal-overview__attention-item">
            {context.loading ? <p role="status">Checking yesterday’s mechanical issues…</p> : issuesError ? (
              <p role="status">Yesterday’s mechanical issues could not be checked. Try Refresh.</p>
            ) : issues.count > 0 ? (
              <>
                <div className="internal-overview__attention-copy"><span className="internal-overview__attention-dot" /><strong>{issues.count} new mechanical {issues.count === 1 ? 'issue was' : 'issues were'} added yesterday</strong></div>
                <p>Added on {displayDate(issues.date)}. Review the building issues and next steps.</p>
                <Link to="/maintenance/mech-management" className="internal-overview__action">Manage mechanical <FaArrowRight aria-hidden="true" /></Link>
              </>
            ) : <p className="internal-overview__clear"><FaCheckCircle aria-hidden="true" /> No new mechanical issues were added yesterday.</p>}
          </div>}
        </section>}
      </div>
    </main>
  );
}
